import { useCallback } from 'react'

interface DualRangeSliderProps {
  /** Absolute minimum value */
  min: number
  /** Absolute maximum value */
  max: number
  /** Step size */
  step: number
  /** Current low value (clamped to [min, highValue]) */
  lowValue: number
  /** Current high value (clamped to [lowValue, max]) */
  highValue: number
  /** Called when the low value changes */
  onLowChange: (v: number) => void
  /** Called when the high value changes */
  onHighChange: (v: number) => void
  /** Format a value for the label display */
  formatLabel?: (v: number, bound: 'low' | 'high') => string
  /** Unit label shown after the range text */
  unit?: string
  /** Label shown above the slider */
  label?: string
}

/**
 * A dual-thumb range slider built from two native range inputs overlaid
 * on a custom track. The filled region between the two thumbs uses the
 * accent colour.
 */
export default function DualRangeSlider({
  min,
  max,
  step,
  lowValue,
  highValue,
  onLowChange,
  onHighChange,
  formatLabel,
  unit = '',
  label,
}: DualRangeSliderProps) {
  const pct = useCallback(
    (v: number) => ((v - min) / (max - min)) * 100,
    [min, max],
  )

  const lowPct = pct(lowValue)
  const highPct = pct(highValue)

  const fmt = formatLabel ?? ((v: number) => String(v))
  const isDefault = lowValue === min && highValue === max

  // Prevent the two thumbs from crossing
  const handleLow = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = Math.min(Number(e.target.value), highValue)
      onLowChange(v)
    },
    [highValue, onLowChange],
  )

  const handleHigh = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = Math.max(Number(e.target.value), lowValue)
      onHighChange(v)
    },
    [lowValue, onHighChange],
  )

  return (
    <div className="space-y-2">
      {/* Header row: label + current range */}
      <div className="flex items-baseline justify-between">
        {label && (
          <span className="text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0]">
            {label}
          </span>
        )}
        <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
          {isDefault ? (
            'Any'
          ) : (
            <>
              {fmt(lowValue, 'low')} – {fmt(highValue, 'high')}
              {unit ? ` ${unit}` : ''}
            </>
          )}
        </span>
      </div>

      {/* Slider track + two range inputs */}
      <div className="relative h-6 flex items-center">
        {/* Background track */}
        <div className="absolute inset-x-0 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700" />

        {/* Filled region between thumbs */}
        <div
          className="absolute h-1.5 rounded-full bg-[#EF8354]"
          style={{ left: `${lowPct}%`, right: `${100 - highPct}%` }}
        />

        {/* Low thumb */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={lowValue}
          onChange={handleLow}
          className="dual-range-thumb absolute inset-x-0 pointer-events-none appearance-none bg-transparent"
          style={{ zIndex: lowPct > 50 ? 5 : 3 }}
        />

        {/* High thumb */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={highValue}
          onChange={handleHigh}
          className="dual-range-thumb absolute inset-x-0 pointer-events-none appearance-none bg-transparent"
          style={{ zIndex: highPct < 50 ? 5 : 3 }}
        />
      </div>

      {/* Min / Max labels */}
      <div className="flex justify-between text-[10px] text-zinc-400 dark:text-zinc-500 -mt-1">
        <span>{fmt(min, 'low')}{unit ? ` ${unit}` : ''}</span>
        <span>{fmt(max, 'high')}{unit ? ` ${unit}` : ''}</span>
      </div>
    </div>
  )
}
